import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { ReaderStory } from '../../api/PublishedEditions';

const TEMPLATE_COLORS: Record<string, string> = {
  news_story: '#1565c0',
  feature_story: '#6a1b9a',
  profile_interview: '#00838f',
  book_review: '#7c3aed',
  movie_review: '#c62828',
  video_game_review: '#283593',
  restaurant_review: '#ea580c',
  recipe: '#ef6c00',
  opinion: '#2e7d32',
  photo_essay: '#37474f',
};

// Map block type IDs to human-readable section headers
const BLOCK_LABELS: Record<string, string> = {
  Title: '',
  Lead: 'The Lead',
  MainIdea: 'Main Idea',
  SupportingDetails: 'Supporting Details',
  Introduction: 'Introduction',
  ClaimOrOpinion: 'My Opinion',
  Evidence: 'Reason & Evidence',
  Conclusion: 'Conclusion',
  SettingAndCharacters: 'Setting & Characters',
  ProblemOrGoal: 'Problem or Goal',
  Attempts: 'What Happened',
  StorySolution: 'Solution',
  Resolution: 'Resolution',
  PeopleAndSetting: 'People & Setting',
  Details: 'Details',
  Cause: 'Cause',
  Effect: 'Effect',
  Compare: 'Compare',
  Contrast: 'Contrast',
  Problem: 'Problem',
  Solution: 'Solution',
  Event: 'Event',
};

interface Props {
  story: ReaderStory;
  index: number;
  total: number;
  newspaperName: string;
}

export default function StoryPage({ story, index, total, newspaperName }: Props) {
  const color = TEMPLATE_COLORS[story.template_type] || '#163567';

  return (
    <Box sx={{
      minHeight: '100vh',
      background: '#fff',
      display: 'flex',
      flexDirection: 'column',
      px: 3,
      py: 4,
      overflowY: 'auto',
    }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="caption" sx={{
          fontFamily: '"Georgia", serif',
          color: '#999',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          fontSize: '0.6rem',
        }}>
          {newspaperName} &middot; {index + 1} of {total}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1, mb: 2 }}>
          <Chip
            label={story.template_label}
            size="small"
            sx={{
              background: color,
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.65rem',
            }}
          />
          {story.is_editor_pick && (
            <Chip
              label="&#11088; Editor's Pick"
              size="small"
              sx={{
                background: '#fff8e1',
                border: '1px solid #ffd54f',
                fontWeight: 600,
                fontSize: '0.65rem',
              }}
            />
          )}
        </Box>

        {/* Title */}
        <Typography sx={{
          fontFamily: '"Georgia", serif',
          fontWeight: 700,
          fontSize: { xs: '1.8rem', sm: '2.2rem' },
          lineHeight: 1.15,
          color: '#1a1a1a',
          mb: 1,
        }}>
          {story.title}
        </Typography>

        {/* Byline */}
        <Typography sx={{
          fontFamily: '"Georgia", serif',
          fontStyle: 'italic',
          color: '#666',
          fontSize: '0.95rem',
        }}>
          By {story.student_name}
        </Typography>

        <Box sx={{ width: 40, height: 2, background: color, mx: 'auto', mt: 2 }} />
      </Box>

      {/* Content blocks */}
      <Box sx={{ maxWidth: 560, mx: 'auto', width: '100%' }}>
        {story.content.blocks.map((block, i) => {
          if (block.type === 'Title') return null; // Already shown above

          const label = BLOCK_LABELS[block.type];
          return (
            <Box key={i} sx={{ mb: 3 }}>
              {label && (
                <Typography variant="overline" sx={{
                  color,
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  letterSpacing: '0.1em',
                  display: 'block',
                  mb: 0.5,
                }}>
                  {label}
                </Typography>
              )}
              <Typography sx={{
                fontFamily: '"Georgia", serif',
                fontSize: '1.05rem',
                lineHeight: 1.75,
                color: '#333',
                whiteSpace: 'pre-wrap',
              }}>
                {block.text}
              </Typography>
            </Box>
          );
        })}

        {/* Images */}
        {story.content.images.map((img, i) => (
          <Box key={`img-${i}`} sx={{ mb: 3, textAlign: 'center' }}>
            <img
              src={img.url}
              alt={img.caption || 'Story image'}
              style={{ maxWidth: '100%', borderRadius: 8 }}
            />
            {img.caption && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                {img.caption}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {/* Footer spacer for swipe area */}
      <Box sx={{ flexGrow: 1 }} />
      <Typography variant="caption" color="text.disabled" sx={{
        textAlign: 'center',
        mt: 4,
        fontFamily: '"Georgia", serif',
      }}>
        Swipe for next story &rarr;
      </Typography>
    </Box>
  );
}
